import React, { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertColor, AlertProps } from '@mui/material/Alert';
import Slide, { SlideProps } from '@mui/material/Slide';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';

function SlideTransition(props: SlideProps) {
    return <Slide {...props} direction="up" />;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
) {
    return (
        <MuiAlert
            elevation={8}
            ref={ref}
            variant="filled"
            {...props}
            sx={{
                borderRadius: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                alignItems: 'center',
                minWidth: 300,
                maxWidth: 500,
                padding: '10px 16px',
                color: '#fff !important',
                '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                    color: '#fff !important',
                    opacity: 1,
                    marginRight: 1.5,
                    padding: 0,
                },
                '& .MuiAlert-message': {
                    padding: '6px 0',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#fff !important',
                    flex: 1,
                },
                '& .MuiAlert-action': {
                    padding: '0 0 0 12px',
                    marginRight: 0,
                    marginLeft: 0,
                    alignItems: 'center',
                    display: 'flex',
                },
                '& .MuiIconButton-root': {
                    color: '#fff !important',
                    padding: '6px',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    }
                },
                ...props.sx,
            }}
        />
    );
});

export interface DefaultMessageProps {
    message?: string;
    detail?: string;
    open: boolean;
    onClose?: () => void;
    type?: AlertColor;
}


export default function DefaultMessage(props: DefaultMessageProps) {

    const [defaultMessage, setDefaultMessage] = React.useState<DefaultMessageProps>(props);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (props.open !== undefined) {
            setDefaultMessage(props);
            setExpanded(false); // Reset expanded state when new message appears
        }
    }, [props]);

    const handleClose = () => {
        if (props.onClose !== undefined) {
            props.onClose();
        }
    };

    return (
        <Snackbar
            open={defaultMessage.open}
            autoHideDuration={6000}
            onClose={handleClose}
            TransitionComponent={SlideTransition}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
            }}
            sx={{
                bottom: { xs: 16, sm: 24 },
                right: { xs: 16, sm: 24 },
            }}
        >
            <Alert
                onClose={handleClose}
                severity={defaultMessage.type !== undefined ? defaultMessage.type : "success"}
                sx={{
                    width: '100%',
                    whiteSpace: 'pre-line',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }}
            >
                <Box sx={{ width: '100%' }}>
                    {/* Linha da mensagem com botão */}
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flex: 1 }}>
                            {defaultMessage.message}
                        </Box>
                        {defaultMessage.detail && (
                            <IconButton
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                sx={{
                                    ml: 1,
                                    p: 0.5,
                                    color: 'inherit'
                                }}
                                aria-label="ver detalhes"
                            >
                                <ExpandMoreIcon
                                    sx={{
                                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s'
                                    }}
                                />
                            </IconButton>
                        )}
                    </Box>

                    {/* Área de detalhes abaixo */}
                    {defaultMessage.detail && (
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{
                                mt: 1.5,
                                pt: 1.5,
                                borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                            }}>
                                <Box sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    mb: 0.5,
                                    opacity: 0.9
                                }}>
                                    Detalhes técnicos:
                                </Box>
                                <Box sx={{
                                    p: 1,
                                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    maxHeight: 150,
                                    overflow: 'auto',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {defaultMessage.detail}
                                </Box>
                            </Box>
                        </Collapse>
                    )}
                </Box>
            </Alert>
        </Snackbar>
    );
}
